import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  canAttemptMasterOtp,
  clearImpersonationStorage,
  fetchMasterUsers,
  getImpersonationMeta,
  getStoredUser,
  isImpersonating,
  isMasterOtpVerified,
  isMasterSession,
  startImpersonation,
  startLocalMasterSwitch,
  stopImpersonation,
} from "../utils/impersonation";
import { resetLogRocketSession } from "../utils/logRocket";

const card = {
  background: "#fff",
  borderRadius: 14,
  border: "1px solid #e5e7eb",
  boxShadow: "0 14px 30px rgba(2, 6, 23, 0.08)",
  padding: 18,
};

const label = { fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 6 };
const input = {
  width: "100%",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  padding: "10px 12px",
  fontSize: 14,
  outline: "none",
};

const tsFrom = (value) => {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  const t = Date.parse(String(value));
  return Number.isFinite(t) ? t : 0;
};

const getLatestEpoch = (item) => {
  const lp = item?.login_payload || {};
  const data = lp?.data || {};
  const candidates = [
    item?.updated_at,
    item?.created_at,
    lp?.updated_at,
    lp?.created_at,
    data?.updated_at,
    data?.created_at,
    data?.restaurant_updated_at,
    data?.restaurant_last_active_at,
    data?.last_order_at,
    data?.otp_verified_at,
  ];
  return candidates.reduce((best, value) => Math.max(best, tsFrom(value)), 0);
};

const formatWhen = (epoch) => {
  if (!epoch) return "N/A";
  try {
    return new Date(epoch).toLocaleString();
  } catch {
    return "N/A";
  }
};

const toneForType = (type) => {
  const t = String(type || "").toLowerCase();
  if (t === "owner") return { bg: "var(--app-primary-soft)", fg: "var(--app-primary)" };
  if (t === "manager") return { bg: "#dcfce7", fg: "#15803d" };
  if (t === "cashier") return { bg: "#fef3c7", fg: "#b45309" };
  return { bg: "#e2e8f0", fg: "#334155" };
};

const AdminImpersonationPage = () => {
  const navigate = useNavigate();
  const adminUser = useMemo(() => getStoredUser(), []);
  const canUseMasterKey =
    isMasterSession(adminUser) ||
    isMasterOtpVerified(adminUser) ||
    canAttemptMasterOtp(adminUser);
  const allowed = canUseMasterKey;

  const [targetMobile, setTargetMobile] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const [targetRestaurantId, setTargetRestaurantId] = useState("");
  const [reason, setReason] = useState("Support troubleshooting");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const [masterUsers, setMasterUsers] = useState([]);
  const [masterUsersLoading, setMasterUsersLoading] = useState(false);
  const [masterQuery, setMasterQuery] = useState("");
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const meta = getImpersonationMeta();
  const active = isImpersonating();

  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onStart = async (e) => {
    e.preventDefault();
    setNotice("");
    setError("");

    if (!targetMobile && !targetUserId && !targetRestaurantId) {
      setError("Provide at least one target identifier: mobile, user ID, or restaurant ID.");
      return;
    }

    setBusy(true);
    try {
      const { message } = await startImpersonation({
        targetMobile: targetMobile.trim(),
        targetUserId: targetUserId.trim(),
        targetRestaurantId: targetRestaurantId.trim(),
        reason: reason.trim(),
      });
      setNotice(message || "Impersonation started.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.message || "Unable to start impersonation.");
    } finally {
      setBusy(false);
    }
  };

  const onLoadMasterUsers = async () => {
    setNotice("");
    setError("");
    setMasterUsersLoading(true);
    try {
      const list = await fetchMasterUsers();
      setMasterUsers(list);
      setNotice(`Loaded ${list.length} accounts.`);
    } catch (err) {
      setError(err?.message || "Unable to load master users.");
    } finally {
      setMasterUsersLoading(false);
    }
  };

  const onSwitchMasterUser = (entry) => {
    setNotice("");
    setError("");
    try {
      startLocalMasterSwitch(entry);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.message || "Unable to switch user.");
    }
  };

  const onStop = async () => {
    setNotice("");
    setError("");
    setBusy(true);
    try {
      const { message } = await stopImpersonation();
      setNotice(message || "Returned to admin session.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.message || "Unable to stop impersonation.");
    } finally {
      setBusy(false);
    }
  };

  const onMasterLogout = () => {
    const ok = window.confirm("Are you sure you want to log out?");
    if (!ok) return;
    clearImpersonationStorage();
    resetLogRocketSession();
    localStorage.removeItem("userData");
    navigate("/login", { replace: true });
  };

  if (!allowed && !active && !canUseMasterKey) {
    return (
      <div style={{ maxWidth: 980, margin: "0 auto", padding: isMobileView ? 10 : 20 }}>
        <div style={card}>
          <h2 style={{ margin: 0, color: "#0f172a" }}>Admin Impersonation</h2>
          <p style={{ color: "#475569", marginTop: 10 }}>
            Your account does not have permission to impersonate users.
          </p>
        </div>
      </div>
    );
  }

  const filteredMasterUsers = masterUsers
    .filter((u) => {
      const q = masterQuery.trim().toLowerCase();
      if (!q) return true;
      const data = u?.login_payload?.data || {};
      const hay =
        `${u?.display_name || ""} ${u?.mobile_number || ""} ${u?.type || ""} ${
          data?.restaurant_name || ""
        } ${data?.name || ""}`.toLowerCase();
      return hay.includes(q);
    })
    .sort((a, b) => getLatestEpoch(b) - getLatestEpoch(a));

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: isMobileView ? 10 : 20, display: "grid", gap: 14 }}>
      <div style={{ ...card, padding: "14px 18px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0, color: "#0f172a" }}>Master Accounts</h2>
            <p style={{ margin: "4px 0 0", color: "#475569", fontSize: 13 }}>
              Use Load Accounts, then open any account. Latest accounts appear first.
            </p>
          </div>
          <button
            type="button"
            onClick={onMasterLogout}
            style={{
              border: "1px solid rgba(239,68,68,.55)",
              borderRadius: 10,
              background: "linear-gradient(135deg,#ef4444,#dc2626)",
              color: "#fff",
              fontWeight: 700,
              padding: "8px 12px",
              cursor: "pointer",
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {active && (
        <div style={{ ...card, borderColor: "#f59e0b", background: "#fffbeb" }}>
          <div style={{ fontWeight: 800, color: "#92400e" }}>Impersonation Active</div>
          <div style={{ marginTop: 8, color: "#7c2d12", fontSize: 14 }}>
            <div>Acting as: <b>{meta?.target?.name || "User"}</b></div>
            <div>Mobile: {meta?.target?.mobile || "-"}</div>
            <div>Started: {meta?.startedAt ? new Date(meta.startedAt).toLocaleString() : "-"}</div>
          </div>
          <button
            type="button"
            onClick={onStop}
            disabled={busy}
            style={{
              marginTop: 12,
              border: "none",
              borderRadius: 10,
              padding: "10px 14px",
              fontWeight: 700,
              cursor: busy ? "not-allowed" : "pointer",
              color: "#fff",
              background: busy ? "#9ca3af" : "#dc2626",
            }}
          >
            {busy ? "Stopping..." : "Stop impersonation"}
          </button>
        </div>
      )}

      {canUseMasterKey && !active && (
        <div style={{ ...card }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <button
              type="button"
              onClick={onLoadMasterUsers}
              disabled={masterUsersLoading}
              style={{
                border: "none",
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 700,
                cursor: masterUsersLoading ? "not-allowed" : "pointer",
                color: "#fff",
                background: masterUsersLoading ? "#94a3b8" : "var(--app-primary)",
              }}
            >
              {masterUsersLoading ? "Loading..." : "Load Accounts"}
            </button>
            <input
              type="text"
              value={masterQuery}
              onChange={(e) => setMasterQuery(e.target.value)}
              placeholder="Search by name, restaurant, mobile"
              style={{ ...input, maxWidth: isMobileView ? "100%" : 360 }}
            />
          </div>

          <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
            {masterUsersLoading ? (
              <div style={{ padding: 14, color: "#64748b", fontSize: 13 }}>Loading accounts...</div>
            ) : filteredMasterUsers.length === 0 ? (
              <div style={{ padding: 14, color: "#64748b", fontSize: 13 }}>No accounts found.</div>
            ) : (
              filteredMasterUsers.map((u) => {
                const tone = toneForType(u?.type);
                const data = u?.login_payload?.data || {};
                return (
                  <div
                    key={u?.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobileView ? "1fr" : "1fr auto",
                      gap: 10,
                      alignItems: isMobileView ? "stretch" : "center",
                      padding: "12px 14px",
                      borderBottom: "1px solid #e2e8f0",
                      background: "#fff",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>
                          {u?.display_name || "User"}
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: tone.bg, color: tone.fg }}>
                          {u?.type || "user"}
                        </span>
                      </div>
                      <div style={{ marginTop: 4, fontSize: 12, color: "#475569" }}>
                        Mobile: {u?.mobile_number || "-"} | Restaurant: {data?.restaurant_name || "-"} | Name: {data?.name || "-"}
                      </div>
                      <div style={{ marginTop: 2, fontSize: 11, color: "#94a3b8" }}>
                        Updated: {formatWhen(getLatestEpoch(u))}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onSwitchMasterUser(u)}
                      style={{
                        border: "none",
                        borderRadius: 8,
                        background: "#0f766e",
                        color: "#fff",
                        fontWeight: 700,
                        padding: "8px 12px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        justifySelf: isMobileView ? "stretch" : "end",
                      }}
                    >
                      Open
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {!canUseMasterKey && (
        <form onSubmit={onStart} style={card}>
          <h3 style={{ margin: 0, color: "#0f172a" }}>Start New Impersonation</h3>
          <p style={{ margin: "8px 0 12px", color: "#64748b", fontSize: 13 }}>
            This requires backend endpoint: <code>/admin/impersonation/start</code>.
          </p>

          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <div style={label}>Target Mobile Number</div>
              <input
                type="text"
                value={targetMobile}
                onChange={(e) => setTargetMobile(e.target.value.replace(/\D/g, ""))}
                placeholder="e.g. 9988776655"
                style={input}
              />
            </div>

            <div>
              <div style={label}>Target User ID (optional)</div>
              <input
                type="text"
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                placeholder="e.g. 4321"
                style={input}
              />
            </div>

            <div>
              <div style={label}>Target Restaurant ID (optional)</div>
              <input
                type="text"
                value={targetRestaurantId}
                onChange={(e) => setTargetRestaurantId(e.target.value)}
                placeholder="e.g. 1234"
                style={input}
              />
            </div>

            <div>
              <div style={label}>Reason</div>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are you impersonating this account?"
                style={{ ...input, resize: "vertical" }}
              />
            </div>
          </div>

          {error && <div style={{ marginTop: 12, color: "#b91c1c", fontWeight: 700 }}>{error}</div>}
          {notice && <div style={{ marginTop: 12, color: "#166534", fontWeight: 700 }}>{notice}</div>}

          <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="submit"
              disabled={busy || active}
              style={{
                border: "none",
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 800,
                cursor: busy || active ? "not-allowed" : "pointer",
                color: "#fff",
                background: busy || active ? "#94a3b8" : "#0f766e",
              }}
            >
              {busy ? "Starting..." : "Start impersonation"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              style={{
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#fff",
                color: "#0f172a",
                fontWeight: 700,
                padding: "10px 14px",
                cursor: "pointer",
              }}
            >
              Back to dashboard
            </button>
          </div>
        </form>
      )}

      {error && <div style={{ color: "#b91c1c", fontWeight: 700 }}>{error}</div>}
      {notice && <div style={{ color: "#166534", fontWeight: 700 }}>{notice}</div>}
    </div>
  );
};

export default AdminImpersonationPage;
